import { RefObject, useEffect, useState } from 'react';

interface ScrollEdgeState {
  showLeftFade: boolean;
  showRightFade: boolean;
  showTopFade: boolean;
  showBottomFade: boolean;
}

/** Horizontal scroll threshold (px) before the left/right fades toggle. */
const SCROLL_THRESHOLD_PX = 1;
/** Vertical scroll threshold (px) before the top/bottom fades toggle. */
const SCROLL_THRESHOLD_PY = 1;

/**
 * Tracks which edges of a scroll container are scrolled away from, so callers can
 * fade them. Reports BOTH axes — horizontal callers read `showLeftFade`/
 * `showRightFade`, vertical callers read `showTopFade`/`showBottomFade`. A single
 * scroll/resize observer drives all four flags; unused axes simply stay `false`
 * when the container can't scroll on that axis.
 */
export const useScrollEdgeFade = <T extends HTMLElement>(
  ref: RefObject<T | null>
): ScrollEdgeState => {
  const [state, setState] = useState<ScrollEdgeState>({
    showLeftFade: false,
    showRightFade: false,
    showTopFade: false,
    showBottomFade: false,
  });

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth, scrollTop, scrollHeight, clientHeight } = node;
      const next: ScrollEdgeState = {
        showLeftFade: scrollLeft > SCROLL_THRESHOLD_PX,
        showRightFade: scrollLeft < scrollWidth - clientWidth - SCROLL_THRESHOLD_PX,
        showTopFade: scrollTop > SCROLL_THRESHOLD_PY,
        showBottomFade: scrollTop < scrollHeight - clientHeight - SCROLL_THRESHOLD_PY,
      };
      setState(prev =>
        prev.showLeftFade === next.showLeftFade &&
        prev.showRightFade === next.showRightFade &&
        prev.showTopFade === next.showTopFade &&
        prev.showBottomFade === next.showBottomFade
          ? prev
          : next
      );
    };

    update();
    node.addEventListener('scroll', update, { passive: true });

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(node);

    return () => {
      node.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [ref]);

  return state;
};
